import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toUrl } from "../shell/routes";
import { chequesApi, type VLedger } from "../lib/api";
import { Combobox, type Option } from "../ui/Combobox";
import { useLoad } from "../ui/useLoad";
import { fmtAmount, parseAmount } from "../lib/money";
import { fmtDate } from "../companies/CompanyPicker";
import { lineSide, newRow, type Form } from "./entry";

// Pieces of the entry screen for sql/263: the cheque on a payment or receipt, costs to bill on to a customer (on a bill,
// payment or journal), and billing those costs on an invoice.

const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// what's wrong with the cheque, or null
export function chequeProblem(f: Form): string | null {
  if (!f.cheque) return null;
  const n = f.cheque.number.trim();
  if (f.base === "receipt" && !n) return "Enter the number of the customer's cheque (the 6 digits on the leaf).";
  if (n && !/^\d{1,10}$/.test(n)) return "A cheque number is digits only (the 6 digits printed on the leaf, e.g. 000123).";
  return null;
}

export function ChequeFields({ f, set, bank, tried }: { f: Form; set: (p: Partial<Form>) => void; bank: boolean; tried: boolean }) {
  if (!bank) return f.cheque ? <p className="notice small">A cheque goes through a bank account: choose the bank in “{f.base === "payment" ? "Paid from" : "Received into"}”.</p> : null;
  const c = f.cheque;
  const issued = f.base === "payment";
  const pdc = !!c && f.date > todayIso();
  const bad = tried && !!chequeProblem(f);
  return (
    <div id="cheque" className="fields inline cheque-strip">
      <label className="check"><input type="checkbox" checked={!!c} onChange={(e) => set({ cheque: e.target.checked ? { number: "", payee: "", drawn_on: "", ac_payee: true } : null })} />
        {issued ? " By cheque" : " Received by cheque"}</label>
      {c && <>
        <label>Cheque no.<input className={`short mono${bad ? " invalid" : ""}`} inputMode="numeric" maxLength={10} value={c.number}
          placeholder={issued ? "Next leaf" : "000123"} onChange={(e) => set({ cheque: { ...c, number: e.target.value } })} /></label>
        {issued ? <>
          <label className="wide-field">Pay to<input maxLength={200} value={c.payee} placeholder="As on the cheque (the account's name if blank)" onChange={(e) => set({ cheque: { ...c, payee: e.target.value } })} /></label>
          <label className="check"><input type="checkbox" checked={c.ac_payee} onChange={(e) => set({ cheque: { ...c, ac_payee: e.target.checked } })} /> A/c payee only</label>
        </> : <label className="wide-field">Drawn on<input maxLength={120} value={c.drawn_on} placeholder="The customer's bank" onChange={(e) => set({ cheque: { ...c, drawn_on: e.target.value } })} /></label>}
        {pdc && <p className="notice small">Post-dated: this {issued ? "payment" : "receipt"} is saved as a draft and posted on {fmtDate(f.date)}, the cheque's date (Banking › Cheques). Use Save draft.</p>}
        {issued && !c.number.trim() && <span className="muted small">The next unused leaf of the bank's cheque book is used; print it once saved.</span>}
        {issued && f.id && <Link className="small" to={toUrl(`banking/cheques/print/${f.id}`)}>Print the cheque</Link>}
      </>}
    </div>
  );
}

// on a bill, payment or journal: which cost lines are to be billed on to a customer
export function BillableBox({ f, set, ledgers }: { f: Form; set: (p: Partial<Form>) => void; ledgers: VLedger[] }) {
  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);
  const customers = useMemo<Option[]>(() => ledgers.filter((l) => l.party && (l.party_kind === "customer" || l.party_kind === "both")).map((l) => ({ value: l.id, label: l.name })), [ledgers]);
  const own = lineSide(f.base);
  const costs = f.lines.map((l, i) => [l, i] as const).filter(([l]) => {
    const g = l.ledger_id ? byId.get(l.ledger_id) : null;
    return g && !g.party && !g.tax && (g.nature === "E" || g.nature === "A") && !g.codes.includes("bank_accounts") && !g.codes.includes("cash_in_hand") && (!own || l.side === own);
  });
  const current = f.lines.find((l) => l.billable?.customer_id)?.billable?.customer_id ?? null;
  const [open, setOpen] = useState(!!current);
  const [cust, setCust] = useState<string | null>(current);
  if (!costs.length) return null;
  const mark = (i: number, on: boolean, who = cust) => set({ lines: f.lines.map((l, j) => (j === i ? { ...l, billable: on && who ? { customer_id: who, project_id: f.project_id ?? null } : null } : l)) });
  if (!open) return <button type="button" className="link small" onClick={() => setOpen(true)}>Bill some of these costs on to a customer…</button>;
  return (
    <fieldset className="section-box billable-box"><legend>Bill on to a customer</legend>
      <label className={`wide-field${!cust && costs.some(([l]) => l.billable) ? " bad" : ""}`}>Customer<Combobox options={customers} value={cust} placeholder="Choose the customer"
        onChange={(v) => { setCust(v); set({ lines: f.lines.map((l) => (l.billable ? { ...l, billable: v ? { customer_id: v, project_id: f.project_id ?? null } : null } : l)) }); }} /></label>
      {costs.map(([l, i]) => <label key={l.key} className="check"><input type="checkbox" disabled={!cust} checked={!!l.billable} onChange={(e) => mark(i, e.target.checked)} />
        {" "}{byId.get(l.ledger_id!)?.name}{l.narration.trim() ? ` · ${l.narration.trim()}` : l.item_name.trim() ? ` · ${l.item_name.trim()}` : ""} <span className="muted">{l.amount ? fmtAmount(parseAmount(l.amount) ?? 0) : ""}</span></label>)}
      <p className="muted small">Once this is posted, the ticked costs wait on the customer: “Bill costs” on their next invoice adds them (at cost, or with a markup).</p>
    </fieldset>
  );
}

// on a sales invoice: the customer's costs waiting to be billed
export function BillCosts({ company, f, set }: { company: string; f: Form; set: (p: Partial<Form>) => void }) {
  const d = useLoad(() => (f.party ? chequesApi.billable(company, f.party) : Promise.resolve(null)), [company, f.party]);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [markup, setMarkup] = useState("");
  const taken = new Set(f.lines.flatMap((l) => l.billable_ids ?? []));
  const waiting = (d.data?.lines ?? []).filter((x) => !taken.has(x.id));
  if (!f.party || !waiting.length) return null;
  const m = markup.trim() ? Number(markup) : 0;
  const badMarkup = markup.trim() !== "" && !(Number.isFinite(m) && m >= 0 && m <= 1000);
  const add = () => {
    if (badMarkup || !pick.size) return;
    const own = lineSide(f.base) ?? "Cr";
    const rows = waiting.filter((x) => pick.has(x.id)).map((x) => {
      const price = Math.round(x.amount * (1 + m / 100) * 100) / 100;
      return { ...newRow(own), ledger_id: d.data?.ledger_id ?? null, item_name: `${x.narration ?? x.account}${x.supplier ? ` (${x.supplier})` : ""}`.slice(0, 200), price: String(price), billable_ids: [x.id] };
    });
    const kept = f.lines.filter((l) => l.ledger_id || l.item_name.trim() || l.price.trim());
    set({ lines: [...kept, ...rows] });
    setOpen(false); setPick(new Set());
  };
  const total = waiting.reduce((t, x) => t + x.amount, 0);
  return (
    <div className="notice billable-notice">
      {!open ? <span>{waiting.length} cost{waiting.length === 1 ? "" : "s"} ({fmtAmount(total)}) waiting to be billed to this customer. <button type="button" className="link" onClick={() => { setOpen(true); setPick(new Set(waiting.map((x) => x.id))); }}>Bill costs…</button></span> : <>
        <table className="grid-table compact"><tbody>{waiting.map((x) => <tr key={x.id}>
          <td><input type="checkbox" checked={pick.has(x.id)} onChange={(e) => { const n = new Set(pick); if (e.target.checked) n.add(x.id); else n.delete(x.id); setPick(n); }} aria-label="Bill this" /></td>
          <td>{fmtDate(x.date)}</td><td className="mono">{x.voucher_number}</td><td>{x.narration ?? x.account}{x.project ? <span className="muted small"> · {x.project}</span> : null}</td><td className="num">{fmtAmount(x.amount)}</td></tr>)}</tbody></table>
        <div className="fields inline">
          <label>Markup %<input className={`num short${badMarkup ? " invalid" : ""}`} inputMode="decimal" value={markup} placeholder="0 (at cost)" onChange={(e) => setMarkup(e.target.value)} /></label>
          <button type="button" className="primary" disabled={!pick.size || badMarkup} onClick={add}>Add {pick.size} to the invoice</button>
          <button type="button" className="link" onClick={() => setOpen(false)}>Back</button>
        </div>
        {badMarkup && <p className="msg err">The markup is a percentage from 0 to 1,000.</p>}
        {!d.data?.ledger_id && <p className="muted small">Choose the income account on each added row.</p>}
      </>}
    </div>
  );
}
